import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import {
  acceptMarketingCookies,
  COOKIE_PREFERENCES_EVENT,
  CONSENT_ACCEPTED,
  getConsent,
  hasConsentChoice,
  rejectMarketingCookies,
} from "@/lib/consent";

export default function CookieConsent() {
  // needsChoice: the visitor has not answered yet. It keeps the banner coming back on
  // every page except the privacy policy, until they accept or reject.
  // preferencesOpen: the banner was reopened on purpose (footer "Cookie preferences").
  const [needsChoice, setNeedsChoice] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setNeedsChoice(!hasConsentChoice());

    const onOpenPreferences = () => setPreferencesOpen(true);
    window.addEventListener(COOKIE_PREFERENCES_EVENT, onOpenPreferences);

    return () => {
      window.removeEventListener(COOKIE_PREFERENCES_EVENT, onOpenPreferences);
    };
  }, []);

  const onPrivacyPage = pathname.replace(/\/+$/, "") === "/privacy";
  const visible = preferencesOpen || (needsChoice && !onPrivacyPage);

  if (!visible) return null;

  const hasExistingChoice = Boolean(getConsent());
  const accepted = getConsent() === CONSENT_ACCEPTED;

  const closeBanner = () => {
    setNeedsChoice(false);
    setPreferencesOpen(false);
  };

  const handleAccept = () => {
    acceptMarketingCookies();
    closeBanner();
  };

  const handleReject = () => {
    rejectMarketingCookies();
    closeBanner();
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="cookie-consent pointer-events-none fixed inset-x-0 bottom-0 z-[10050] px-3 pb-3 sm:px-6 sm:pb-6"
    >
      <div className="cookie-consent-card pointer-events-auto relative mx-auto max-h-full w-full max-w-[1040px] overflow-y-auto rounded-[18px] border border-[var(--rivyou-line)] border-l-[3px] border-l-[var(--rivyou-accent)] bg-[var(--rivyou-surface)] shadow-[0_22px_60px_rgba(14,29,21,0.18)]">
        {hasExistingChoice && (
          <span
            data-testid="cookie-consent-status"
            className={`absolute right-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold ${
              accepted
                ? "border-[var(--rivyou-accent)] bg-[var(--rivyou-teal-light)] text-[var(--rivyou-accent-dark)]"
                : "border-[var(--rivyou-line)] bg-[var(--rivyou-surface-2)] text-[var(--rivyou-muted)]"
            }`}
          >
            {accepted ? (
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <ShieldOff className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {accepted ? "You've accepted cookies" : "You've rejected cookies"}
          </span>
        )}
        <div className="cookie-consent-layout grid gap-5 p-5 sm:grid-cols-[1fr_auto] sm:items-end sm:p-6">
          <div className="min-w-0">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[var(--rivyou-line)] bg-[var(--rivyou-surface-2)] px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.12em] text-[var(--rivyou-accent-dark)]">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Privacy choice
            </div>
            <h2 className="font-heading text-[22px] font-bold leading-tight text-[var(--rivyou-ink)] sm:text-2xl">
              {hasExistingChoice ? "Update cookie preferences" : "Cookies, kept transparent."}
            </h2>
            <p className="mt-3 max-w-[560px] text-sm leading-relaxed text-[var(--rivyou-copy)] sm:text-[15px]">
              We use optional marketing cookies to measure ad performance and improve campaigns. You can accept them or
              continue without them.{" "}
              <Link
                to="/privacy"
                onClick={() => {
                  setNeedsChoice(true);
                  setPreferencesOpen(false);
                }}
                className="font-bold text-[var(--rivyou-accent-dark)] underline decoration-[var(--rivyou-line)] underline-offset-4 transition-colors hover:text-[var(--rivyou-accent)]"
              >
                Read our privacy policy
              </Link>
            </p>
          </div>

          <div className="cookie-consent-actions grid shrink-0 grid-cols-1 gap-2 sm:min-w-[270px] sm:grid-cols-2 sm:items-center">
            <button
              type="button"
              onClick={handleReject}
              data-testid="cookie-consent-reject"
              className="min-h-11 rounded-full border border-[var(--rivyou-line)] bg-[var(--rivyou-surface)] px-5 py-2.5 text-sm font-bold text-[var(--rivyou-ink)] transition-colors hover:border-[var(--rivyou-accent)] hover:bg-[var(--rivyou-teal-light)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rivyou-accent)]"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={handleAccept}
              data-testid="cookie-consent-accept"
              className="min-h-11 rounded-full border border-[var(--rivyou-accent)] bg-[var(--rivyou-accent)] px-5 py-2.5 text-sm font-bold text-white shadow-[0_12px_24px_rgba(5,150,105,0.22)] transition-colors hover:border-[var(--rivyou-accent-dark)] hover:bg-[var(--rivyou-accent-dark)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rivyou-accent)]"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
